/* eslint-disable import/no-cycle */
import Http from '@/api/http';
import Devtool from '@/api/Devtool';
import CustomerDetail from '@/api/CustomerDetail';
/* eslint-enable import/no-cycle */

export default class Customer {
    /**
     * customer detail API
     */
    static detail = CustomerDetail;

    /**
     * check before password recovery
     * @param ssoId
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static checkRecoverPassword(ssoId) {
        return Http.get(`/customer/${ssoId}/recover/password/check`);
    }

    /**
     * recover password
     * @param ssoId
     * @param evalancheStoreId
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static recoverPassword(ssoId, evalancheStoreId) {
        const url = `/customer/${ssoId}/recover/password`;
        if (evalancheStoreId) {
            return Http.post(url, { evalancheStoreId });
        }
        return Http.post(url);
    }

    /**
     * update customer login/email
     * @param ssoId
     * @param body
     * @return Promise
     *
     * @author Lukas Wilkes <lukas.wilkes@hhlab.de>
     */
    static updateCustomerEmail(ssoId, body) {
        const url = `/customer/${ssoId}/email/update`;
        return Http.put(url, body);
    }

    /**
     * updates a email in sap to celeraone login mail
     * @param ssoId
     * @param gpnumber
     *
     * @return Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static updateMailInSap(ssoId, gpnumber) {
        return Http.put(`/customer/${ssoId}/email/sap/update`, {
            gpnumber,
        });
    }

    /**
     * get a user by the given sso ID
     * @param ssoId
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static getBySsoId(ssoId) {
        return Http.get(`/customer/by/sso/${ssoId}`);
    }

    /**
     * get customers masterdata by sso ID
     * @param ssoId
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static getMasterdata(ssoId) {
        return Http.get(`/customer/${ssoId}/masterdata`);
    }

    /**
     * get customer masterdata for checkup
     * @param ssoId
     * @returns Promise
     *
     * @author Lukas Wilkes <lukas.wilkes@hhlab.de>
     */
    static getCheckMasterdata(ssoId) {
        return Http.get(`/customer/${ssoId}/masterdata/check`);
    }

    /**
     * update customer masterdata
     * @param ssoId
     * @param masterdata
     * @param systems
     * @returns Promise
     *
     * @author Lukas Wilkes <lukas.wilkes@hhlab.de>
     */
    static updateMasterdata(ssoId, masterdata, systems) {
        return Http.put(`/customer/${ssoId}/masterdata/update`, {
            masterdata,
            systems,
        });
    }

    /**
     * checks if a customers exist in celeraone, evalanche and salesforce
     * @param ssoId
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static checkCustomer(ssoId) {
        return Http.get(`/customer/${ssoId}/check`);
    }

    /**
     * get a user by gp ID from SAP
     * @param gpId
     * @param scope
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static getByGpId(gpId, scope) {
        return Http.get(`/customer/by/gp/${gpId}/${scope}`);
    }

    /**
     * get all sap purchases
     * @param ids
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static getSapPurchases(ids) {
        return Http.post('/customer/sap/purchases', {
            ids,
        });
    }

    /**
     * transfer all user purchases
     * @param ssoId
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static transferPurchases(ssoId) {
        return Http.get(`/customer/${ssoId}/transfer/purchases`);
    }

    /**
     * tansfers a SAP Customer to celeraone and evalanche
     * @param data - has to be an object { email, gpnumber }
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static createCustomerFromSap(data) {
        return Http.post('/customer/create/from/sap', data);
    }

    /**
     * creates a new Customer in celeraone and evalanche
     * @param data - has to be an object {
     *      portal, // shz, svz, nnn, prignitzer
     *      password, // min-length 6
     *      email, // valid and does not exist in celeraone
     *      gender, // female or male
     *      firstName, // string min-length 3
     *      lastName, // string min-length 3
     * }
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static createNewCustomer(data) {
        return Http.post('/customer/create/new', data);
    }

    /**
     * transfer orders from one sap user to one celeraone user
     * @param ssoId
     * @param gpId
     * @param scope
     * @param deleteContractor?
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static linkUserToOrder(ssoId, gpId, scope, deleteContractor = true) {
        return Http.get(`/customer/${ssoId}/link/to/order/${gpId}/${scope}`, {
            params: {
                deleteContractor,
            },
        });
    }

    /**
     * delete a gpnumber form an celeraone user
     * @param ssoId
     * @param gpId
     * @param scope
     * @return Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static deleteCustomerGp(ssoId, gpId, scope, original) {
        return Http.delete(`/customer/${ssoId}/delete/${gpId}/${scope}/${original}`);
    }

    /**
     * activate a customer if his status is registered
     * @param ssoId
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static activateCustomer(ssoId) {
        return Http.get(`/customer/${ssoId}/activate`);
    }

    /**
     * deletes a customer by email
     * @param email
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static deleteByMail({
        email,
        inEvalanche,
        inCeleraone,
        inSalesforce,
    }) {
        return Devtool.deleteUserByMail(email, { inEvalanche, inCeleraone, inSalesforce });
    }

    /**
     * gets all customer sessions
     * @param ssoId
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static getSessions(ssoId) {
        return Http.get(`/customer/sessions/${ssoId}`);
    }

    /**
     * terminates a global session by sessionId
     * @param sessionId
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static terminateSession(sessionId) {
        return Http.delete(`/customer/terminate/session/${sessionId}`);
    }

    /**
     * terminates a global session by sessionId
     * @param sessionId
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static terminateAllSessions(ssoId) {
        return Http.delete(`/customer/terminate/all/sessions/${ssoId}`);
    }

    /**
     * set customers password
     * @param ssoId
     * @param password
     * @returns Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static setPassword(ssoId, password) {
        return Http.post(`/customer/${ssoId}/set/password`, {
            password,
        });
    }

    /**
     * looking for a user in celeraone and evalanche
     * @param email
     * @return Promise
     *
     * @author Johann Prenzler <johann.prenzler@noz-digital.de>
     */
    static find(email) {
        return Http.post('/customer/find', { email });
    }

    /**
     * gets a list with informations for all linked accounts of a customer
     * @param ssoId
     * @returns Promise
     *
     * @author Lukas Wilkes <lukas.wilkes@hhlab.de>
     */
    static getLinkedAccounts(ssoId) {
        return Http.get(`/customer/${ssoId}/linked/accounts`);
    }

    /**
     * removes a linked account (atom) from a customer
     * @param ssoId
     * @param atomId
     * @returns Promise
     *
     * @author Lukas Wilkes <lukas.wilkes@hhlab.de>
     */
    static removeLinkedAccount(ssoId, atomId) {
        return Http.delete(`/customer/${ssoId}/linked/account/${atomId}`);
    }

    /**
     * create a linked account (atom) for a customer
     * @param ssoId
     * @param data
     * @returns Promise
     *
     * @author Lukas Wilkes <lukas.wilkes@hhlab.de>
     */
    static createLinkedAccount(ssoId, data) {
        return Http.post(`/customer/${ssoId}/linked/account`, data);
    }

    /**
     * check the email of a customer in all systems
     * @param ssoId
     * @param email
     * @returns Promise
     *
     * @author Lukas Wilkes <lukas.wilkes@hhlab.de>
     */
    static checkCustomerEmail(ssoId, email) {
        return Http.post(`/customer/${ssoId}/email/check`, { email });
    }
}
